import {
  alpha,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { useCustomerAssortments } from '@retail/app/stores/selected-context';
import { ContainedButton, OutlinedButton, TextButton } from '@shared/components';
import { useState } from 'react';

interface Props {
  itemsToAddCount: number;
  isSaving: boolean;
  onAddItems: (customerAssortmentIds: number[]) => void;
  onClose: () => void;
  goBack: () => void;
}

export function SelectCustomerAssortments({
  itemsToAddCount,
  isSaving,
  onAddItems,
  onClose,
  goBack,
}: Props) {
  const t = useAppTFunction();
  const customerAssortments = useCustomerAssortments();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const allSelected =
    customerAssortments.length > 0 && selectedIds.length === customerAssortments.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggle = (assortmentId: number) =>
    setSelectedIds((ids) =>
      ids.includes(assortmentId) ? ids.filter((id) => id !== assortmentId) : [...ids, assortmentId]
    );

  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : customerAssortments.map(({ assortmentId }) => assortmentId));

  return (
    <>
      <DialogContent>
        <Stack gap={3}>
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="h6">{t('myAssortment.actions.customerAssortments')}</Typography>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={t('myAssortment.actions.itemsToAddCount', { count: itemsToAddCount })}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('myAssortment.actions.addToCustomerAssortments')}
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              py={0.5}
              bgcolor="grey.50"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                  />
                }
                label={
                  <Typography variant="subtitle2">
                    {t('myAssortment.actions.selectAllCustomerAssortments')}
                  </Typography>
                }
              />
              <Typography variant="body2" color="text.secondary">
                {t('myAssortment.actions.customerAssortmentsSelected', {
                  selected: selectedIds.length,
                  total: customerAssortments.length,
                })}
              </Typography>
            </Stack>
            <Divider />
            <Stack maxHeight={360} overflow="auto" divider={<Divider />}>
              {customerAssortments.map(({ assortmentId, name }) => {
                const checked = selectedIds.includes(assortmentId);
                return (
                  <FormControlLabel
                    key={assortmentId}
                    control={<Checkbox checked={checked} onChange={() => toggle(assortmentId)} />}
                    label={name}
                    sx={(theme) => {
                      const tint = (opacity: number) =>
                        theme.vars
                          ? `rgba(${theme.vars.palette.secondary.mainChannel} / ${opacity})`
                          : alpha(theme.palette.secondary.main, opacity);
                      return {
                        m: 0,
                        px: 2,
                        py: 0.5,
                        transition: 'background-color 150ms',
                        bgcolor: checked ? tint(0.08) : 'transparent',
                        '&:hover': {
                          bgcolor: tint(checked ? 0.12 : 0.04),
                        },
                      };
                    }}
                  />
                );
              })}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>
          <OutlinedButton color="secondary" size="small" onClick={goBack}>
            {t('common.goBack')}
          </OutlinedButton>
          <ContainedButton
            color="secondary"
            loading={isSaving}
            size="small"
            onClick={() => onAddItems(selectedIds)}
          >
            {t('myAssortment.actions.addItems')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </>
  );
}
